import { useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent, PointerEvent } from "react";
import type { HistoryDay } from "../../shared/history";
import { START_SCORE } from "../../shared/score";
import { addDays, daysBetween } from "../../shared/time";
import type { KidSummary } from "../../shared/types";
import { formatDay, shortDate } from "../format";
import { kidStyle } from "../kid-style";
import { monotonePath } from "../curve";
import { KidMarker, MarkerShape } from "./KidMarker";

export const CHART_RANGES = [1, 4, 12, 52] as const;
export type ChartWeeks = (typeof CHART_RANGES)[number];

/** One kid's line: who they are, their marker shape, and their days, oldest first (may start after `from`). */
export interface ChartSeries {
  kid: Pick<KidSummary, "id" | "name" | "colour" | "goal">;
  shape: number;
  days: HistoryDay[];
}

const HEIGHT = 240;
const PAD = { top: 12, right: 14, bottom: 26, left: 34 };

type Tick = { i: number; label: string };

/**
 * Every day for 1 week, week starts (Mondays) for 4 and 12 weeks, month starts for 52, thinned
 * to every 2nd or 3rd when a narrow phone would make them collide.
 */
function xTicks(from: string, n: number, weeks: ChartWeeks, pxPerDay: number): Tick[] {
  if (weeks === 1) {
    return Array.from({ length: n + 1 }, (_, i) => ({ i, label: formatDay(addDays(from, i), { weekday: "short" }) }));
  }
  const months = weeks === 52;
  const all: Tick[] = [];
  for (let i = 0; i <= n; i++) {
    const day = addDays(from, i);
    if (months ? day.endsWith("-01") : new Date(day + "T12:00:00Z").getUTCDay() === 1) {
      const label = formatDay(day, months ? { month: "short" } : { day: "numeric", month: "short" });
      all.push({ i, label });
    }
  }
  const minGap = months ? 34 : 52;
  const step = Math.max(1, Math.ceil(minGap / (pxPerDay * (months ? 30 : 7))));
  // Counted back from the newest, so the most recent week or month always keeps its label.
  return all.filter((_, j) => (all.length - 1 - j) % step === 0);
}

/**
 * The bottom of the y axis. 0 normally; when every score and goal on screen is above 50 it
 * zooms to the nearest 10 below the lowest (70 for a low of 73), so a family that lives in the
 * 90s still sees the line move. Always at least 20 points tall.
 */
export function chartFloor(values: readonly number[]): number {
  const low = Math.min(...values);
  if (!(low > 50)) return 0;
  return Math.min(80, Math.floor((low - 2) / 10) * 10);
}

/** A five-point star around 0,0 for star days on the 1- and 4-week charts, drawn once. */
const STAR = Array.from({ length: 10 }, (_, k) => {
  const r = k % 2 === 0 ? 6 : 2.6;
  const a = ((k * 36 - 90) * Math.PI) / 180;
  return `${(r * Math.cos(a)).toFixed(2)},${(r * Math.sin(a)).toFixed(2)}`;
}).join(" ");

/**
 * Every chosen kid's score as a line over 1, 4, 12 or 52 weeks, in their colour, with the goal
 * dashed behind them, star days marked, and today as a bigger dot at each line's head. Tap or
 * drag to read every kid's score on any day. Inline SVG drawn at the container's real pixel
 * width, so strokes keep their weight and text never stretches. Each line sweeps in from the
 * left when it first appears (not with reduced motion).
 */
export function ScoreChart({
  series,
  from,
  today,
  weeks,
}: {
  series: ChartSeries[];
  from: string;
  today: string;
  weeks: ChartWeeks;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const pill = useRef<HTMLSpanElement>(null);
  const [width, setWidth] = useState(0);
  const [pillWidth, setPillWidth] = useState(0);
  // The day offset (from `from`) being read, or null for "today".
  const [active, setActive] = useState<number | null>(null);
  const dragging = useRef(false);
  // Focused by a finger rather than the keyboard: no focus ring then, since the finger shows where it is.
  const [byPointer, setByPointer] = useState(false);

  useLayoutEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const measure = () => setWidth(Math.floor(el.clientWidth));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The readout is as wide as its text, which changes with the day; measured so it stays on screen.
  useLayoutEffect(() => {
    if (pill.current) setPillWidth(pill.current.offsetWidth);
  });

  // No tap yet means every score has sat at 100 the whole time: flat lines say nothing.
  const empty = series.every((s) => s.days.every((d) => d.taps === 0 && d.end === START_SCORE));
  const n = daysBetween(from, today);
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const pxPerDay = n > 0 ? plotW / n : 0;

  const goals = [...new Set(series.map((s) => s.kid.goal))].sort((a, b) => b - a);
  const floor = chartFloor([...series.flatMap((s) => s.days.map((d) => d.end)), ...goals]);
  const zoomed = floor > 0;
  const yTicks = zoomed
    ? Array.from({ length: (100 - floor) / 10 + 1 }, (_, k) => floor + k * 10)
    : [0, 25, 50, 75, 100];

  const x = (i: number) => Math.round((PAD.left + (n > 0 ? i * pxPerDay : plotW)) * 10) / 10;
  const y = (v: number) => Math.round((PAD.top + (1 - (v - floor) / (100 - floor)) * plotH) * 10) / 10;

  const lines = series.map((s) => {
    const points = s.days.map((d) => ({ i: daysBetween(from, d.day), d }));
    return {
      ...s,
      points,
      byOffset: new Map(points.map((p) => [p.i, p.d])),
      path: monotonePath(points.map((p) => ({ x: x(p.i), y: y(p.d.end) }))),
    };
  });
  const first = Math.min(n, ...lines.map((l) => l.points[0]?.i ?? n));
  const shown = active ?? n;
  const readings = lines
    .map((l) => ({ kid: l.kid, shape: l.shape, day: l.byOffset.get(shown) }))
    .filter((r): r is { kid: ChartSeries["kid"]; shape: number; day: HistoryDay } => r.day !== undefined);

  function pick(e: PointerEvent<SVGSVGElement>) {
    if (pxPerDay === 0) return;
    const r = e.currentTarget.getBoundingClientRect();
    const i = Math.round((e.clientX - r.left - PAD.left) / pxPerDay);
    setActive(Math.min(n, Math.max(first, i)));
  }

  // ←/→ step a day, Home and End jump to the first day and today, as the finger does along it.
  function onKey(e: KeyboardEvent<SVGSVGElement>) {
    const next =
      e.key === "ArrowLeft" ? shown - 1 : e.key === "ArrowRight" ? shown + 1 : e.key === "Home" ? first : e.key === "End" ? n : null;
    if (next === null) return;
    e.preventDefault();
    setByPointer(false);
    setActive(Math.min(n, Math.max(first, next)));
  }

  const ticks = xTicks(from, n, weeks, pxPerDay);
  const shownDate = addDays(from, shown);
  const pillLeft = Math.min(Math.max(x(shown), pillWidth / 2), width - pillWidth / 2);
  const sameGoal = goals.length === 1;
  // Goal labels sit just above their line at the left; one too close to the last is left out.
  let lastLabel = -Infinity;
  const goalKeys: { goal: number; colour: string | null }[] = sameGoal
    ? [{ goal: goals[0], colour: null }]
    : series.map((s) => ({ goal: s.kid.goal, colour: s.kid.colour }));
  const goalLabels = goalKeys
    .sort((a, b) => b.goal - a.goal)
    .filter((g, j, all) => all.findIndex((o) => o.goal === g.goal) === j)
    .filter((g) => {
      if (y(g.goal) - lastLabel < 16) return false;
      lastLabel = y(g.goal);
      return true;
    });
  const label = `Score over the last ${weeks === 1 ? "week" : `${weeks} weeks`}${zoomed ? `, zoomed to ${floor} to 100` : ""}: ${series
    .map((s) => `${s.kid.name} ${s.days.at(-1)?.end ?? START_SCORE} now, goal ${s.kid.goal}`)
    .join("; ")}. Left and right arrows read each day.`;
  const dayName = shownDate === today ? "Today" : shortDate(shownDate);
  const spoken = `${dayName}: ${readings.map((r) => `${r.kid.name} ${r.day.end}`).join(", ")}`;

  return (
    <div className="chart" ref={wrap}>
      {empty ? (
        <p className="hint chart__empty">The graph starts when the first points are tapped.</p>
      ) : (
        <>
          <ul className="chart__legend" aria-hidden="true">
            {series.map((s) => (
              <li key={s.kid.id} className="chart__key" style={kidStyle(s.kid.colour)}>
                <KidMarker shape={s.shape} />
                {s.kid.name}
              </li>
            ))}
            {zoomed && <li className="chart__zoom">{`${floor}–100 shown`}</li>}
          </ul>
          {/* The readout for screen readers: quiet until a day is chosen, then each new day once. */}
          <p className="visually-hidden" aria-live="polite">
            {active !== null ? spoken : ""}
          </p>
          <div className="chart__readout" aria-hidden="true">
            {readings.length > 0 && (
              <span className="chart__pill" ref={pill} style={{ left: pillLeft }}>
                <span>{dayName}</span>
                {readings.map((r) => (
                  <span key={r.kid.id} className="chart__reading" style={kidStyle(r.kid.colour)}>
                    <KidMarker shape={r.shape} />
                    {r.day.end}
                  </span>
                ))}
              </span>
            )}
          </div>
          {width > 0 && (
            <svg
              className="chart__svg"
              width={width}
              height={HEIGHT}
              viewBox={`0 0 ${width} ${HEIGHT}`}
              role="img"
              aria-label={label}
              tabIndex={0}
              onKeyDown={onKey}
              onBlur={() => setByPointer(false)}
              data-pointer={byPointer || undefined}
              onPointerDown={(e) => {
                setByPointer(true);
                dragging.current = true;
                e.currentTarget.setPointerCapture(e.pointerId);
                pick(e);
              }}
              onPointerMove={(e) => {
                // A mouse reads as it moves; a finger only while it is down.
                if (dragging.current || e.pointerType === "mouse") pick(e);
              }}
              onPointerUp={() => (dragging.current = false)}
              onPointerCancel={() => (dragging.current = false)}
            >
              {yTicks.map((v) => (
                <g key={v}>
                  <line className="chart__grid" x1={PAD.left} x2={width - PAD.right} y1={y(v)} y2={y(v)} />
                  <text className="chart__axis" x={PAD.left - 8} y={y(v)} textAnchor="end" dominantBaseline="middle">
                    {v}
                  </text>
                </g>
              ))}
              {zoomed && (
                // The usual axis-break mark, so a line near the bottom is not read as near 0.
                <path
                  className="chart__break"
                  d={`M${PAD.left - 4} ${y(floor) + 9}l8 -5M${PAD.left - 4} ${y(floor) + 14}l8 -5`}
                />
              )}
              {ticks.map((t) => {
                const tx = x(t.i);
                const anchor = tx < PAD.left + 18 ? "start" : tx > width - PAD.right - 18 ? "end" : "middle";
                return (
                  <text key={t.i} className="chart__axis" x={tx} y={HEIGHT - 6} textAnchor={anchor}>
                    {t.label}
                  </text>
                );
              })}

              <g className="chart__reveal">
                {sameGoal ? (
                  <line className="chart__goal" x1={PAD.left} x2={width - PAD.right} y1={y(goals[0])} y2={y(goals[0])} />
                ) : (
                  series.map((s) => (
                    <line
                      key={s.kid.id}
                      className="chart__goal chart__goal--kid"
                      style={kidStyle(s.kid.colour)}
                      x1={PAD.left}
                      x2={width - PAD.right}
                      y1={y(s.kid.goal)}
                      y2={y(s.kid.goal)}
                    />
                  ))
                )}
                {goalLabels.map((g) => (
                  <text
                    key={g.goal}
                    className={g.colour ? "chart__goal-label chart__goal-label--kid" : "chart__goal-label"}
                    style={g.colour ? kidStyle(g.colour) : undefined}
                    x={PAD.left + 4}
                    y={y(g.goal) - 7}
                  >
                    {`goal ${g.goal}`}
                  </text>
                ))}
              </g>

              {active !== null && (
                <line className="chart__cursor" x1={x(shown)} x2={x(shown)} y1={PAD.top} y2={PAD.top + plotH} />
              )}

              {lines.map((l, k) => {
                const head = l.byOffset.get(n);
                return (
                  <g
                    key={l.kid.id}
                    className="chart__reveal"
                    style={{ ...kidStyle(l.kid.colour), animationDelay: `${k * 90}ms` } as CSSProperties}
                  >
                    <path className="chart__line" d={l.path} />
                    {l.points
                      .filter((p) => p.d.star && p.i !== n)
                      .map((p) =>
                        weeks <= 4 ? (
                          <polygon
                            key={p.d.day}
                            className="chart__star"
                            points={STAR}
                            transform={`translate(${x(p.i)} ${y(p.d.end)})`}
                          />
                        ) : (
                          <circle
                            key={p.d.day}
                            className="chart__star-dot"
                            cx={x(p.i)}
                            cy={y(p.d.end)}
                            r={weeks === 12 ? 2.6 : 1.8}
                          />
                        ),
                      )}
                    {head && <MarkerShape shape={l.shape} className="chart__today" cx={x(n)} cy={y(head.end)} r={7} />}
                  </g>
                );
              })}

              {active !== null &&
                readings.map((r) => (
                  <g key={r.kid.id} style={kidStyle(r.kid.colour)}>
                    <MarkerShape shape={r.shape} className="chart__marker" cx={x(shown)} cy={y(r.day.end)} r={6} />
                  </g>
                ))}
            </svg>
          )}
        </>
      )}
    </div>
  );
}
